/**
 * Nutrition math for a meal on Today: its totals with coverage (how many of its
 * foods had each nutrient), the header's at-a-glance macros, and each amount's
 * share of the person's daily target.
 */
import type { Entry, TargetView } from '../../shared/api';
import {
  BUILTIN_NUTRIENTS,
  formatAmount,
  scaleMap,
  shownDecimals,
  sumWithCoverage,
  type GoalDirection,
  type NutrientDef,
  type NutrientMap,
  type TotalsWithCoverage,
} from '../../shared/nutrients';
import { kcal } from './format';

type Coverage = Pick<TotalsWithCoverage, 'known' | 'count'>;

/** A meal's totals, and how many of its foods had a value for each nutrient. */
export function mealNutrition(entries: readonly Pick<Entry, 'per100' | 'grams'>[]): TotalsWithCoverage {
  return sumWithCoverage(entries.map((e) => scaleMap(e.per100, e.grams)));
}

/**
 * Nutrient keys in the nutrient list's order (built-ins, then the person's own), so every
 * meal and food lists them the same way. `defs` is that list by key, in order; while it's
 * still loading, the built-ins' order. Keys it doesn't know go last, in the order given.
 */
export function inListOrder(keys: readonly string[], defs: ReadonlyMap<string, unknown>): string[] {
  const order = defs.size > 0 ? [...defs.keys()] : BUILTIN_NUTRIENTS.map((n) => n.key);
  const index = new Map(order.map((key, i) => [key, i]));
  const rank = (key: string) => index.get(key) ?? Number.MAX_SAFE_INTEGER;
  return [...keys].sort((a, b) => rank(a) - rank(b));
}

/** Some of the summed items have no value for this nutrient, so its total is only "at least" (≥). */
export function isPartial(key: string, coverage: Coverage): boolean {
  // Own keys only: a key like "constructor" mustn't read Object's built-in.
  const known = Object.hasOwn(coverage.known, key) ? coverage.known[key]! : 0;
  return known < coverage.count;
}

/** A value rounded the way the list shows it (formatAmount), so a row's share agrees with its amount. */
export function shownAmount(value: number, def: Pick<NutrientDef, 'decimals'>): number {
  // Through Intl like formatAmount: it rounds the shortest decimal form (36.565 → 36.57), toFixed the binary one (36.56).
  return Number(value.toLocaleString('en-US', { maximumFractionDigits: shownDecimals(value, def), useGrouping: false }));
}

/**
 * How a daily target reads in a share: a limit for "at most", a goal for "at least" and
 * "aim for". Today already calls the calorie target (an "aim for") the "Goal".
 */
export const SHARE_WORDS = { min: 'goal', max: 'limit', target: 'goal' } as const satisfies Record<GoalDirection, string>;
export type ShareWord = (typeof SHARE_WORDS)[GoalDirection];

export interface TargetShare {
  /** The share, rounded to a whole percent. */
  percent: number;
  /** More than nothing but under 1%: shown as "<1%" rather than "0%". */
  underOne: boolean;
  word: ShareWord;
}

/** What share of a daily target an amount is. Null without an amount, or without a target above zero. */
export function targetShare(amount: number | undefined, target: Pick<TargetView, 'amount' | 'direction'> | undefined): TargetShare | null {
  if (amount === undefined || !Number.isFinite(amount) || amount < 0) return null;
  if (!target || target.amount === null || !Number.isFinite(target.amount) || target.amount <= 0) return null;
  const exact = (amount / target.amount) * 100;
  const percent = Math.round(exact);
  // Own keys only, so a stray direction like "constructor" can't read Object's built-ins.
  const word = Object.hasOwn(SHARE_WORDS, target.direction) ? SHARE_WORDS[target.direction] : 'goal';
  return { percent, underOne: percent === 0 && exact > 0, word };
}

export interface ShareLabel {
  text: string;
  spoken: string;
}

/**
 * A share as shown ("21% of goal") and as a screen reader should say it ("less than 1% of
 * limit"). From a partial sum it's only a lower bound ("≥22% of limit"); under 1% of a
 * partial sum would claim an upper bound the data can't back, so there's no label.
 */
export function shareLabel(share: TargetShare, partial = false): ShareLabel | null {
  const pct = `${share.percent.toLocaleString('en-US')}%`;
  if (partial) return share.underOne ? null : { text: `≥${pct} of ${share.word}`, spoken: `at least ${pct} of ${share.word}` };
  if (share.underOne) return { text: `<1% of ${share.word}`, spoken: `less than 1% of ${share.word}` };
  const text = `${pct} of ${share.word}`;
  return { text, spoken: text };
}

/** One nutrient row's share of its daily target, worked out from the amount as shown. */
export function rowShare(
  value: number | undefined,
  def: Pick<NutrientDef, 'decimals'>,
  target: Pick<TargetView, 'amount' | 'direction'> | undefined,
  partial: boolean,
): ShareLabel | null {
  if (value === undefined || !Number.isFinite(value)) return null;
  const share = targetShare(shownAmount(value, def), target);
  return share && shareLabel(share, partial);
}

export interface MacroItem {
  key: 'kcal' | 'protein' | 'carbs' | 'fat';
  /** As shown: "37 g protein", or "≥37 g protein" when some foods have no protein data. */
  text: string;
  /** As a screen reader says it: "at least 37 g protein". */
  spoken: string;
}

/**
 * A meal header's glance: calories, then protein, carbs and fat in whole grams. A macro that
 * none of the meal's foods has data for is left out rather than shown as 0; one that only
 * some have is marked "≥", like the full list.
 */
export function macroSummary(totals: NutrientMap, coverage?: Coverage): MacroItem[] {
  const item = (key: MacroItem['key'], text: string): MacroItem => {
    const atLeast = coverage !== undefined && totals[key] !== undefined && isPartial(key, coverage);
    return { key, text: atLeast ? `≥${text}` : text, spoken: atLeast ? `at least ${text}` : text };
  };
  const items = [item('kcal', `${kcal(totals.kcal)} kcal`)];
  for (const key of ['protein', 'carbs', 'fat'] as const) {
    const value = totals[key];
    if (value !== undefined && Number.isFinite(value)) items.push(item(key, `${formatAmount(value, { decimals: 0 })} g ${key}`));
  }
  return items;
}
